import PageToolBar from '../../../components/PageToolBar';
import React, {useEffect, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import { validTaxIDNumber } from '@mayo/utils';
import {IncomeTaxRegistrationUnit} from '../../../api/types';
import {
  ErrorMessage,
  FormGroup,
  FormItemGroup,
  Input,
  RequireMarker,
} from '../../../../../components/form-items';
import Block from './block';
import {IconButton} from '../../../../../components/Button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle} from '@fortawesome/pro-light-svg-icons';
import {v4 as uuid} from 'uuid';
import {DeleteDialog} from '../../../components/Dialog';

const IncomeTax = function () {
  const {
    control,
    register,
    getValues,
    errors,
    trigger,
    formState: {isSubmitted},
  } = useFormContext();
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const {fields, append, remove} = useFieldArray<IncomeTaxRegistrationUnit>({
    name: 'incomeTaxRegistrationUnits',
    control,
  });
  useEffect(() => {
    if (fields.length === 0) {
      append({incomeTaxReturnUnitId: uuid()});
    }
  }, [fields]);
  return (
    <div>
      <PageToolBar pageTitle={'所得稅申報單位'} />
      <Block>
        {fields.map((field, idx) => (
          <div
            data-test-id={`income-id-${idx}`}
            key={field.id}
            className={
              'border-b border-b-[#e9eced] pb-5 mb-5 last:border-b-0 last-mb-0'
            }
          >
            <div
              className={'grid items-center grid-cols-[1fr,65px] gap-x-[15px]'}
            >
              <div>
                <FormGroup columnTemplates={'1fr 1fr 1fr 210px 1fr 180px'}>
                  <input
                    className={'min-w-0'}
                    data-test-id={'income-tax-return-unit-id'}
                    type={'hidden'}
                    ref={register()}
                    name={`incomeTaxRegistrationUnits.${idx}.incomeTaxReturnUnitId`}
                    defaultValue={field.incomeTaxReturnUnitId}
                  />
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.declarationName`}
                    >
                      申報單位名稱 <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'declaration-name'}
                      name={`incomeTaxRegistrationUnits.${idx}.declarationName`}
                      id={`incomeTaxRegistrationUnits.${idx}.declarationName`}
                      ref={register({
                        required: '限輸入一個以上的中文字',
                        validate: {
                          isHans: v => {
                            return (/^[\u4E00-\u9FFF]+$/i.test(v) && v.length >= 2) || '限輸入一個以上的中文字';
                          },
                        },
                      })}
                      maxLength={50}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.declarationName
                          ?.message
                      }
                      defaultValue={field.declarationName}
                      placeholder={'XXXX單位'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.declarationName
                          ?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.unifiedBusinessNo`}
                    >
                      統一編號(數字) <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'unified-business-no'}
                      name={`incomeTaxRegistrationUnits.${idx}.unifiedBusinessNo`}
                      id={`incomeTaxRegistrationUnits.${idx}.unifiedBusinessNo`}
                      ref={register({
                        required: '必填欄位',
                        pattern: {
                          value: /^\d{8}$/,
                          message: '限制為8碼數字',
                        },
                        validate: {
                          isValid: v =>
                            validTaxIDNumber(v) || '請輸入正確統一編號',
                          isRepeat: v => {
                            const {incomeTaxRegistrationUnits} = getValues();
                            return incomeTaxRegistrationUnits.some(
                              (
                                tax: IncomeTaxRegistrationUnit,
                                compareIdx: number,
                              ) =>
                                tax.unifiedBusinessNo === v &&
                                compareIdx !== idx,
                            )
                              ? '統一編號不可重覆'
                              : true;
                          },
                        },
                      })}
                      maxLength={8}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.unifiedBusinessNo?.message
                      }
                      defaultValue={field.unifiedBusinessNo}
                      placeholder={'12345678'}
                      onBlur={e => {
                        if (isSubmitted) {
                          trigger(
                            fields
                              .map(
                                (f, fIdx) =>
                                  `incomeTaxRegistrationUnits.${fIdx}.unifiedBusinessNo`,
                              )
                              .filter((f, fIdx) => fIdx !== idx),
                          );
                        }
                      }}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.unifiedBusinessNo?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.collectionAuthorityCode`}
                    >
                      稽徵機關代碼
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'collection-authority-code'}
                      name={`incomeTaxRegistrationUnits.${idx}.collectionAuthorityCode`}
                      id={`incomeTaxRegistrationUnits.${idx}.collectionAuthorityCode`}
                      ref={register({
                        required: '必填欄位',
                        pattern: {
                          value: /^[A-Z]\d{2}$/,
                          message: '第一碼為大寫英文，後兩碼為數字',
                        },
                      })}
                      maxLength={3}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.collectionAuthorityCode?.message
                      }
                      defaultValue={field.collectionAuthorityCode}
                      placeholder={'A03'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.collectionAuthorityCode?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.declarationHouseLogicSerialNumber`}
                    >
                      申報單位房屋稅籍編號(英數字) <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'declaration-house-logic-serial-number'}
                      name={`incomeTaxRegistrationUnits.${idx}.declarationHouseLogicSerialNumber`}
                      id={`incomeTaxRegistrationUnits.${idx}.declarationHouseLogicSerialNumber`}
                      ref={register({
                        required: '必填欄位',
                        pattern: {
                          value: /^\w{12}$/,
                          message: '限制為12碼英數字',
                        },
                      })}
                      maxLength={12}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.declarationHouseLogicSerialNumber?.message
                      }
                      defaultValue={field.declarationHouseLogicSerialNumber}
                      placeholder={'051105105410'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.declarationHouseLogicSerialNumber?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.deductObligor`}
                    >
                      扣費義務人姓名 <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'deduct-obligor'}
                      name={`incomeTaxRegistrationUnits.${idx}.deductObligor`}
                      id={`incomeTaxRegistrationUnits.${idx}.deductObligor`}
                      ref={register({
                        required: '必填欄位',
                        maxLength: {
                          value: 50,
                          message: '字數限制為 50 字',
                        },
                      })}
                      maxLength={50}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.deductObligor
                          ?.message
                      }
                      defaultValue={field.deductObligor}
                      placeholder={'王大明'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.deductObligor
                          ?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup className={'min-w-0'}>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.withholdingUnitLogicSerialNumber`}
                    >
                      扣繳單位稅籍編號(英數字) <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'with-holding-unit-logic-serial-number'}
                      name={`incomeTaxRegistrationUnits.${idx}.withholdingUnitLogicSerialNumber`}
                      id={`incomeTaxRegistrationUnits.${idx}.withholdingUnitLogicSerialNumber`}
                      ref={register({
                        required: '必填欄位',
                        pattern: {
                          value: /^\w{9}$/,
                          message: '限制為9碼英數字',
                        },
                      })}
                      maxLength={9}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.withholdingUnitLogicSerialNumber?.message
                      }
                      defaultValue={field.withholdingUnitLogicSerialNumber}
                      placeholder={'123456789'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.withholdingUnitLogicSerialNumber?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                </FormGroup>
                <FormGroup columnTemplates={4}>
                  <FormItemGroup>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.declarationAddress`}
                    >
                      申報單位地址
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'declaration-address'}
                      name={`incomeTaxRegistrationUnits.${idx}.declarationAddress`}
                      id={`incomeTaxRegistrationUnits.${idx}.declarationAddress`}
                      ref={register({
                        required: '必填欄位',
                        maxLength: {
                          value: 26,
                          message: '字數限制為 26 字',
                        },
                      })}
                      maxLength={26}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.declarationAddress?.message
                      }
                      defaultValue={field.declarationAddress}
                      placeholder={'XX縣XX里XX路XX號'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]
                          ?.declarationAddress?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                 <FormItemGroup>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.responsiblePersonName`}
                    >
                      負責人
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'responsible-person-name'}
                      name={`incomeTaxRegistrationUnits.${idx}.responsiblePersonName`}
                      id={`incomeTaxRegistrationUnits.${idx}.responsiblePersonName`}
                      ref={register({
                        required: '必填欄位',
                        maxLength: {
                          value: 20,
                          message: '字數限制 20 字',
                        },
                      })}
                      maxLength={20}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.responsiblePersonName
                          ?.message
                      }
                      defaultValue={field.responsiblePersonName}
                      placeholder={'王大明'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.responsiblePersonName
                          ?.message}
                    </ErrorMessage>
                  </FormItemGroup> 
                  <FormItemGroup>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.contactName`}
                    >
                      聯絡人
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'concat-name'}
                      name={`incomeTaxRegistrationUnits.${idx}.contactName`}
                      id={`incomeTaxRegistrationUnits.${idx}.contactName`}
                      ref={register({
                        required: '必填欄位',
                        maxLength: {
                          value: 20,
                          message: '字數限制 20 字',
                        },
                      })}
                      maxLength={20}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.contactName
                          ?.message
                      }
                      defaultValue={field.contactName}
                      placeholder={'王大明'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.contactName
                          ?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup>
                    <label
                      htmlFor={`incomeTaxRegistrationUnits.${idx}.mobilePhone`}
                    >
                      聯絡電話
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'mobile-phone'}
                      name={`incomeTaxRegistrationUnits.${idx}.mobilePhone`}
                      id={`incomeTaxRegistrationUnits.${idx}.mobilePhone`}
                      ref={register({
                        required: '必填欄位',
                      })}
                      maxLength={10}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.mobilePhone
                          ?.message
                      }
                      defaultValue={field.mobilePhone}
                      placeholder={'0123456789'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.mobilePhone
                          ?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                  <FormItemGroup>
                    <label htmlFor={`incomeTaxRegistrationUnits.${idx}.email`}>
                      Email
                      <RequireMarker />
                    </label>
                    <Input
                      data-test-id={'email'}
                      name={`incomeTaxRegistrationUnits.${idx}.email`}
                      id={`incomeTaxRegistrationUnits.${idx}.email`}
                      ref={register({
                        required: '必填欄位',
                        pattern: {
                          value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                          message: '格式不正確',
                        },
                      })}
                      maxLength={30}
                      error={
                        errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.email?.message
                      }
                      defaultValue={field.email}
                      placeholder={'test@example.com'}
                    />
                    <ErrorMessage>
                      {errors?.incomeTaxRegistrationUnits &&
                        errors.incomeTaxRegistrationUnits[idx]?.email?.message}
                    </ErrorMessage>
                  </FormItemGroup>
                </FormGroup>
              </div>
              <div className={'flex items-center'}>
                {fields.length !== 1 && (
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      setDeleteIdx(idx);
                    }}
                    data-test-id={'remove-btn'}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'trash-alt']}
                      style={{fontSize: 16}}
                    />
                  </IconButton>
                )}
                {idx === fields.length - 1 && (
                  <IconButton
                    onClick={() => append({incomeTaxReturnUnitId: uuid()})}
                    data-test-id={'add-btn'}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      style={{fontSize: 16}}
                    />
                  </IconButton>
                )}
              </div>
            </div>
          </div>
        ))}
        <DeleteDialog
          isOpen={deleteIdx !== null}
          onCancel={() => setDeleteIdx(null)}
          onConfirm={() => {
            if (deleteIdx !== null) {
              remove(deleteIdx);
            }

            setDeleteIdx(null);
          }}
        />
      </Block>
    </div>
  );
};

export default IncomeTax;
